/**
 * Zakładka "Wydatki" (Etap 3 planu KSeF/koszty): zakupy towarowe + koszty zewnętrzne
 * vs sprzedaż/marża per miesiąc / sklep / kategoria, ujęcie memoriałowe albo kasowe.
 * Dane: raportWydatkowService.pobierz -> {grupy, razem, platnosci, udzial_ksef, meta}.
 * Standard UI: 12px/11px, padding 6px 12px / 4px 10px, tylko tokeny kolorów var(--...),
 * zaznaczony przełącznik = pełne tło + #fff INLINE (wymóg motywu).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { raportWydatkowService } from '../../services/raportWydatkowService';

const p2 = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;

export const PRESETY = [
  { id: 'miesiac', etykieta: 'Ten miesiąc', zakres: (dz) => ({ od: iso(new Date(dz.getFullYear(), dz.getMonth(), 1)), do: iso(dz) }) },
  { id: 'poprzedni', etykieta: 'Poprzedni miesiąc', zakres: (dz) => ({ od: iso(new Date(dz.getFullYear(), dz.getMonth() - 1, 1)), do: iso(new Date(dz.getFullYear(), dz.getMonth(), 0)) }) },
  { id: 'kwartal', etykieta: 'Kwartał', zakres: (dz) => ({ od: iso(new Date(dz.getFullYear(), Math.floor(dz.getMonth() / 3) * 3, 1)), do: iso(dz) }) },
  { id: 'rok', etykieta: 'Rok', zakres: (dz) => ({ od: iso(new Date(dz.getFullYear(), 0, 1)), do: iso(dz) }) },
];

export const GRUPOWANIA = [
  { id: 'miesiac', etykieta: 'Miesiąc' },
  { id: 'sklep', etykieta: 'Sklep' },
  { id: 'kategoria', etykieta: 'Kategoria' },
];
export const UJECIA = [
  { id: 'memorial', etykieta: 'Memoriał (data wystawienia)' },
  { id: 'kasa', etykieta: 'Kasa (data zapłaty)' },
];
export const KLUCZE = [
  { id: 'centrala', etykieta: 'Centrala osobno' },
  { id: 'rowno', etykieta: 'Po równo na sklepy' },
  { id: 'obrot', etykieta: 'Po obrocie' },
];

const NAZWY_MIESIECY = ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec', 'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'];

export const kwota = (v) => {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const etykietaGrupy = (g) => {
  const m = String(g?.klucz || '').match(/^(\d{4})-(\d{2})$/);
  if (m) return `${NAZWY_MIESIECY[Number(m[2]) - 1] || m[2]} ${m[1]}`;
  return g?.etykieta || String(g?.klucz ?? '—');
};

// --- style (tokeny) ---
const pole = { padding: '4px 6px', fontSize: '11px', border: '1px solid var(--border)', borderRadius: '4px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' };
const etyk = { fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 };
const th = { padding: '5px 8px', textAlign: 'right', borderBottom: '2px solid var(--border)', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const td = { padding: '4px 8px', borderBottom: '1px solid var(--border)', fontSize: '11px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const tdL = { ...td, textAlign: 'left', fontWeight: 600 };
const kolorWyniku = (v) => (Number(v) < 0 ? 'var(--danger)' : 'var(--success)');

const Przelacznik = ({ opcje, wartosc, onZmiana, nazwa, zablokowane }) => (
  <div role="group" aria-label={nazwa} style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
    {opcje.map((o) => {
      const jest = o.id === wartosc;
      return (
        <button
          key={o.id}
          type="button"
          aria-pressed={jest}
          disabled={zablokowane}
          data-testid={`${nazwa}-${o.id}`}
          onClick={() => onZmiana(o.id)}
          style={{
            padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '12px', cursor: 'pointer',
            border: '1px solid var(--primary)',
            background: jest ? 'var(--primary)' : 'var(--card-bg)',
            color: jest ? '#fff' : 'var(--primary)',
          }}
        >
          {o.etykieta}
        </button>
      );
    })}
  </div>
);

const Kafelek = ({ etykieta, wartosc, kolor, testId, dopisek }) => (
  <div data-testid={testId} style={{ backgroundColor: 'var(--card-bg)', borderLeft: `3px solid ${kolor}`, borderRadius: '4px', padding: '8px 12px', minWidth: '150px', flex: '1 1 150px' }}>
    <div style={{ fontSize: '16px', fontWeight: 700, color: kolor, fontVariantNumeric: 'tabular-nums' }}>{kwota(wartosc)} zł</div>
    <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{etykieta}</div>
    {dopisek && <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>{dopisek}</div>}
  </div>
);

/** Prosty wykres słupkowy CSS: marża / koszty / wynik per grupa (bez biblioteki). */
const Wykres = ({ grupy }) => {
  const serie = [
    { klucz: 'marza', etykieta: 'Marża', kolor: 'var(--success)' },
    { klucz: 'koszty_razem', etykieta: 'Koszty', kolor: 'var(--warning)' },
    { klucz: 'wynik', etykieta: 'Wynik', kolor: 'var(--info)' },
  ];
  const maks = Math.max(1, ...grupy.flatMap((g) => serie.map((s) => Math.abs(Number(g[s.klucz]) || 0))));
  if (!grupy.length) return null;
  return (
    <div data-testid="wydatki-wykres" className="card" style={{ fontSize: '11px', marginTop: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <span style={{ fontSize: '13px', fontWeight: 600 }}>Marża, koszty i wynik</span>
        <div style={{ display: 'flex', gap: '10px' }}>
          {serie.map((s) => (
            <span key={s.klucz} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--text-secondary)' }}>
              <span style={{ width: '10px', height: '10px', background: s.kolor, borderRadius: '2px', display: 'inline-block' }} />{s.etykieta}
            </span>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', height: '160px', overflowX: 'auto', padding: '0 4px' }}>
        {grupy.map((g) => (
          <div key={String(g.klucz)} data-testid="wykres-grupa" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '72px', flex: '1 0 72px', height: '100%' }}>
            <div style={{ display: 'flex', gap: '3px', alignItems: 'flex-end', flex: 1, width: '100%', justifyContent: 'center' }}>
              {serie.map((s) => {
                const v = Number(g[s.klucz]) || 0;
                const h = Math.round((Math.abs(v) / maks) * 100);
                return (
                  <div
                    key={s.klucz}
                    title={`${s.etykieta}: ${kwota(v)} zł`}
                    aria-label={`${etykietaGrupy(g)} ${s.etykieta} ${kwota(v)} zł`}
                    style={{ width: '18px', height: `${Math.max(h, v === 0 ? 0 : 2)}%`, background: s.kolor, opacity: v < 0 ? 0.45 : 1, borderRadius: '2px 2px 0 0', borderBottom: v < 0 ? '2px solid var(--danger)' : 'none' }}
                  />
                );
              })}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px', whiteSpace: 'nowrap' }}>{etykietaGrupy(g)}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>Słupek wyblakły z czerwoną podstawą = wartość ujemna.</div>
    </div>
  );
};

export default function WydatkiTab({ locations = [], initialLocationId = '', dzis = new Date() }) {
  const [preset, setPreset] = useState('miesiac');
  const [zakres, setZakres] = useState(() => PRESETY[0].zakres(dzis));
  const [sklep, setSklep] = useState(initialLocationId ? String(initialLocationId) : '');
  const [grupuj, setGrupuj] = useState('miesiac');
  const [ujecie, setUjecie] = useState('memorial');
  const [klucz, setKlucz] = useState('centrala');
  const [dane, setDane] = useState(null);
  const [laduje, setLaduje] = useState(false);
  const [blad, setBlad] = useState('');
  const [eksportuje, setEksportuje] = useState(false);
  const zadanieRef = useRef(0); // numer ostatniego zapytania - spóźniona odpowiedź starszego nie nadpisuje nowszej

  const params = useMemo(() => ({
    od: zakres.od, do: zakres.do, location_id: sklep || '', grupuj, ujecie, klucz_ogolnych: klucz,
  }), [zakres, sklep, grupuj, ujecie, klucz]);

  const pobierz = useCallback(async () => {
    const nr = ++zadanieRef.current;
    setLaduje(true);
    setBlad('');
    try {
      const r = await raportWydatkowService.pobierz(params);
      if (nr !== zadanieRef.current) return;
      setDane(r);
    } catch (e) {
      if (nr !== zadanieRef.current) return;
      setDane(null);
      setBlad(e?.data?.error || e?.data?.komunikat || e?.message || 'Nie udało się pobrać raportu');
    } finally {
      if (nr === zadanieRef.current) setLaduje(false);
    }
  }, [params]);

  useEffect(() => { pobierz(); }, [pobierz]);

  const wybierzPreset = (id) => {
    const p = PRESETY.find((x) => x.id === id);
    if (!p) return;
    setPreset(id);
    setZakres(p.zakres(dzis));
  };
  const zmienDate = (pole_, wartosc) => {
    if (!wartosc) return;
    setPreset('wlasny');
    setZakres((z) => ({ ...z, [pole_]: wartosc }));
  };

  const eksportuj = async () => {
    setEksportuje(true);
    try {
      await raportWydatkowService.eksportCsv(params);
    } catch (e) {
      setBlad(e?.data?.error || e?.message || 'Eksport CSV nie powiódł się');
    } finally {
      setEksportuje(false);
    }
  };

  const kategorie = dane?.meta?.kategorie || [];
  const grupy = dane?.grupy || [];
  const razem = dane?.razem;
  const platnosci = dane?.platnosci;
  const ksef = dane?.udzial_ksef;
  const uwagi = dane?.meta?.uwagi || [];
  const pokazKlucz = grupuj === 'sklep' || !!sklep || grupuj === 'kategoria';
  const poKategorii = grupuj === 'kategoria';

  return (
    <div data-testid="wydatki-tab" style={{ fontSize: '12px' }}>
      {/* Filtry */}
      <div className="card" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 18px', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
          <span style={etyk}>Zakres</span>
          <Przelacznik nazwa="preset" opcje={PRESETY} wartosc={preset} onZmiana={wybierzPreset} />
          <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            od{' '}
            <input type="date" aria-label="Data od" value={zakres.od} max={zakres.do} onChange={(e) => zmienDate('od', e.target.value)} style={pole} />
          </label>
          <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            do{' '}
            <input type="date" aria-label="Data do" value={zakres.do} min={zakres.od} onChange={(e) => zmienDate('do', e.target.value)} style={pole} />
          </label>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={etyk}>Sklep</span>
          <select aria-label="Sklep" value={sklep} onChange={(e) => setSklep(e.target.value)} style={{ ...pole, minWidth: '140px' }}>
            <option value="">Cała firma</option>
            {locations.map((l) => <option key={l.id} value={String(l.id)}>{l.nazwa}</option>)}
          </select>
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={etyk}>Grupuj</span>
          <Przelacznik nazwa="grupuj" opcje={GRUPOWANIA} wartosc={grupuj} onZmiana={setGrupuj} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={etyk}>Ujęcie</span>
          <Przelacznik nazwa="ujecie" opcje={UJECIA} wartosc={ujecie} onZmiana={setUjecie} />
        </div>
        {pokazKlucz && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={etyk}>Koszty ogólne</span>
            <Przelacznik nazwa="klucz" opcje={KLUCZE} wartosc={klucz} onZmiana={setKlucz} />
          </div>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
          <button type="button" onClick={pobierz} disabled={laduje} data-testid="btn-odswiez" style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--border)', color: 'var(--text-secondary)', background: 'var(--card-bg)', borderRadius: '4px', cursor: 'pointer' }}>
            Odśwież
          </button>
          <button type="button" data-testid="btn-eksport-csv" onClick={eksportuj} disabled={laduje || eksportuje || !dane} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--primary)', color: '#fff', background: 'var(--primary)', borderRadius: '4px', cursor: 'pointer' }}>
            {eksportuje ? 'Eksportuję…' : 'Eksport CSV'}
          </button>
        </div>
      </div>

      {blad && (
        <div data-testid="wydatki-blad" style={{ background: 'var(--danger-light)', color: 'var(--danger)', padding: '6px 12px', borderRadius: '4px', marginBottom: '12px', fontSize: '12px' }}>{blad}</div>
      )}
      {laduje && <div data-testid="wydatki-laduje" style={{ color: 'var(--text-muted)', padding: '12px' }}>Ładowanie raportu…</div>}

      {dane && razem && (
        <>
          {/* Kafelki */}
          <div data-testid="wydatki-kafelki" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
            <Kafelek testId="kafelek-sprzedaz" etykieta="Sprzedaż netto" wartosc={razem.sprzedaz_netto} kolor="var(--info)" />
            <Kafelek testId="kafelek-marza" etykieta="Marża (po FIFO)" wartosc={razem.marza} kolor="var(--success)" />
            <Kafelek testId="kafelek-zakupy" etykieta="Zakupy towarowe netto" wartosc={razem.zakupy_towarowe_netto} kolor="var(--purple)" dopisek="ile kupiliśmy - nie odejmowane od wyniku" />
            <Kafelek testId="kafelek-koszty" etykieta="Koszty razem netto" wartosc={razem.koszty_razem} kolor="var(--warning)" />
            <Kafelek testId="kafelek-wynik" etykieta="Wynik = marża − koszty" wartosc={razem.wynik} kolor={kolorWyniku(razem.wynik)} />
          </div>
          {ujecie === 'kasa' && platnosci && (
            <div data-testid="wydatki-platnosci" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
              <Kafelek testId="kafelek-zaplacone" etykieta="Zapłacone (brutto)" wartosc={platnosci.zaplacone} kolor="var(--success)"
                dopisek={platnosci.kw_kasa_liczba ? `w tym ${platnosci.kw_kasa_liczba} KW z kasy bez kosztu: ${kwota(platnosci.kw_kasa_poza_rejestrem)} zł` : undefined} />
              <Kafelek testId="kafelek-do-zaplaty" etykieta={`Do zapłaty (brutto, ${platnosci.do_zaplaty_liczba ?? 0} dok.)`} wartosc={platnosci.do_zaplaty} kolor="var(--warning)" />
              <Kafelek testId="kafelek-przeterminowane" etykieta={`Przeterminowane (brutto, ${platnosci.przeterminowane_liczba ?? 0} dok.)`} wartosc={platnosci.przeterminowane} kolor="var(--danger)" />
            </div>
          )}

          {/* Tabela */}
          <div className="card" style={{ overflow: 'auto' }}>
            <table data-testid="wydatki-tabela" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                {poKategorii ? (
                  <tr>
                    <th style={{ ...th, textAlign: 'left' }}>Kategoria</th>
                    <th style={th}>Koszty sklepów</th>
                    <th style={th}>Koszty ogólne (Centrala)</th>
                    <th style={th}>Razem netto</th>
                    <th style={th}>Udział %</th>
                  </tr>
                ) : (
                  <tr>
                    <th style={{ ...th, textAlign: 'left' }}>{grupuj === 'sklep' ? 'Sklep' : 'Miesiąc'}</th>
                    <th style={th}>Sprzedaż netto</th>
                    <th style={th} title="Koszt FIFO sprzedanych pozycji (jak w Rentowności)">Koszt sprz. towarów</th>
                    <th style={th}>Marża</th>
                    <th style={th} title="Faktury zakupowe minus korekty, bez UFR - ile kupiliśmy (zapas), nie koszt sprzedanych">Zakupy towarowe</th>
                    {kategorie.map((k) => <th key={k} style={th}>{k}</th>)}
                    {(grupuj === 'sklep' || !!sklep) && <th style={th} title="Część kosztów bez sklepu przypisana wg klucza">w tym ogólne</th>}
                    <th style={th}>Koszty razem</th>
                    <th style={th}>Wynik</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {grupy.length === 0 && (
                  <tr><td colSpan={poKategorii ? 5 : 7 + kategorie.length} style={{ ...td, textAlign: 'center', color: 'var(--text-muted)' }}>Brak danych w zakresie</td></tr>
                )}
                {grupy.map((g) => (
                  poKategorii ? (
                    <tr key={String(g.klucz)} data-testid="wydatki-wiersz">
                      <td style={tdL}>{g.etykieta}</td>
                      <td style={td}>{kwota(g.koszty_lokalne)}</td>
                      <td style={td}>{kwota(g.koszty_ogolne)}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{kwota(g.koszty_razem)}</td>
                      <td style={td}>{kwota(g.udzial_procent)} %</td>
                    </tr>
                  ) : (
                    <tr key={String(g.klucz)} data-testid="wydatki-wiersz" style={g.centrala ? { background: 'var(--bg)' } : undefined}>
                      <td style={tdL}>{etykietaGrupy(g)}{g.kod ? <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> {g.kod}</span> : null}</td>
                      <td style={td}>{kwota(g.sprzedaz_netto)}</td>
                      <td style={td}>{kwota(g.koszt_sprzedanych_towarow)}</td>
                      <td style={{ ...td, color: 'var(--success)', fontWeight: 600 }}>{kwota(g.marza)}</td>
                      <td style={td} title={`faktury ${kwota(g.zakupy_faktury_netto)} + korekty ${kwota(g.korekty_netto)}`}>{kwota(g.zakupy_towarowe_netto)}</td>
                      {kategorie.map((k) => <td key={k} style={td}>{kwota(g.koszty?.[k] ?? 0)}</td>)}
                      {(grupuj === 'sklep' || !!sklep) && <td style={{ ...td, color: 'var(--text-muted)' }}>{kwota(g.koszty_ogolne)}</td>}
                      <td style={{ ...td, color: 'var(--warning)', fontWeight: 600 }}>{kwota(g.koszty_razem)}</td>
                      <td style={{ ...td, color: kolorWyniku(g.wynik), fontWeight: 700 }}>{kwota(g.wynik)}</td>
                    </tr>
                  )
                ))}
              </tbody>
              <tfoot>
                {poKategorii ? (
                  <tr data-testid="wydatki-suma" style={{ background: 'var(--bg)' }}>
                    <td style={tdL}>Razem</td>
                    <td style={{ ...td, fontWeight: 700 }}>{kwota(Number(razem.koszty_razem) - Number(razem.koszty_ogolne || 0))}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{kwota(razem.koszty_ogolne)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{kwota(razem.koszty_razem)}</td>
                    <td style={td}>100,00 %</td>
                  </tr>
                ) : (
                  <tr data-testid="wydatki-suma" style={{ background: 'var(--bg)' }}>
                    <td style={tdL}>Razem</td>
                    <td style={{ ...td, fontWeight: 700 }}>{kwota(razem.sprzedaz_netto)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{kwota(razem.koszt_sprzedanych_towarow)}</td>
                    <td style={{ ...td, fontWeight: 700, color: 'var(--success)' }}>{kwota(razem.marza)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{kwota(razem.zakupy_towarowe_netto)}</td>
                    {kategorie.map((k) => <td key={k} style={{ ...td, fontWeight: 700 }}>{kwota(razem.koszty?.[k] ?? 0)}</td>)}
                    {(grupuj === 'sklep' || !!sklep) && <td style={{ ...td, fontWeight: 700, color: 'var(--text-muted)' }}>{kwota(razem.koszty_ogolne)}</td>}
                    <td style={{ ...td, fontWeight: 700, color: 'var(--warning)' }}>{kwota(razem.koszty_razem)}</td>
                    <td style={{ ...td, fontWeight: 700, color: kolorWyniku(razem.wynik) }}>{kwota(razem.wynik)}</td>
                  </tr>
                )}
              </tfoot>
            </table>
            {poKategorii && (
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', padding: '6px 8px' }}>
                W okresie: sprzedaż netto {kwota(razem.sprzedaz_netto)} zł, marża {kwota(razem.marza)} zł, zakupy towarowe {kwota(razem.zakupy_towarowe_netto)} zł, wynik {kwota(razem.wynik)} zł.
              </div>
            )}
            {!!sklep && klucz === 'centrala' && razem.koszty_ogolne_centrala !== undefined && (
              <div data-testid="wydatki-centrala" style={{ fontSize: '11px', color: 'var(--text-secondary)', padding: '6px 8px' }}>
                Koszty ogólne (Centrala) w okresie, nieprzypisane do tego sklepu: {kwota(razem.koszty_ogolne_centrala)} zł netto.
              </div>
            )}
          </div>

          {!poKategorii && <Wykres grupy={grupy} />}

          {/* Udział KSeF + adnotacja metodyczna */}
          <div data-testid="wydatki-uwagi" className="card" style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '12px' }}>
            {ksef && (
              <div data-testid="wydatki-ksef" style={{ marginBottom: '6px' }}>
                <strong>Udział KSeF:</strong> {ksef.dostepne === false
                  ? 'niedostępny'
                  : `${ksef.dokumentow_z_ksef ?? 0} dokumentów z KSeF, ${ksef.recznie ?? 0} wpisanych ręcznie / z innych źródeł (koszty: ${ksef.koszty_z_ksef ?? 0} / ${ksef.koszty_recznie ?? 0}, zakupy: ${ksef.zakupy_z_ksef ?? 0} / ${ksef.zakupy_inne ?? 0})`}
              </div>
            )}
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Jak liczymy</div>
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              <li><strong>Sprzedaż i marża</strong> - jak w Rentowności: paragony po rabacie, bez kaucji, zwroty odjęte raz, koszt sprzedanych towarów z FIFO; plus faktury sprzedaży bez paragonu.</li>
              <li><strong>Zakupy towarowe</strong> - faktury zakupowe minus korekty (bez anulowanych, Import Optima i serii UFR). To „ile kupiliśmy” (zapas), <strong>nie</strong> koszt sprzedanych towarów - dlatego nie odejmujemy ich od wyniku drugi raz.</li>
              <li><strong>Koszty</strong> - koszty zewnętrzne netto per kategoria; koszty powiązane z fakturą zakupową pominięte (są w zakupach). Koszty bez sklepu = Centrala, dzielone wg wybranego klucza.</li>
              <li><strong>Wynik</strong> = marża − koszty razem. Ujęcie kasowe: koszty tylko zapłacone (data zapłaty), zakupy wg terminu płatności z faktury; kafelki Zapłacone / Do zapłaty / Przeterminowane w brutto.</li>
              {uwagi.map((u, i) => <li key={i} data-testid="wydatki-uwaga">{u}</li>)}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
